import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import ReactECharts from 'echarts-for-react';

const QuickGraphs = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [cashFlowData, setCashFlowData] = useState(null);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [dashResponse, graphsResponse] = await Promise.all([
        axios.get(`${API}/sheets/Dashboard`),
        axios.get(`${API}/sheets/Graphs`)
      ]);
      
      setDashboardData(dashResponse.data.data);
      setCashFlowData(graphsResponse.data.data);
    } catch (error) {
      // Silent fail for quick graphs
    }
  };

  const parseProductData = () => {
    if (!cashFlowData || cashFlowData.length < 5) return { categories: [], data: [] };
    
    const categories = [];
    const data = [];
    let startRow = -1;
    
    for (let i = 0; i < cashFlowData.length; i++) {
      if (cashFlowData[i][0] === 'Product' && cashFlowData[i][1] === 'Revenue') {
        startRow = i + 1;
        break;
      }
    }
    
    if (startRow > 0) {
      for (let i = startRow; i < startRow + 3 && i < cashFlowData.length; i++) {
        if (cashFlowData[i][0]) {
          categories.push(cashFlowData[i][0]);
          data.push(parseFloat(cashFlowData[i][1]?.toString().replace(/,/g, '')) || 0);
        }
      }
    }
    
    return { categories, data };
  };

  const parseBridgeData = () => {
    if (!cashFlowData || cashFlowData.length < 10) return { categories: [], data: [] };
    
    const categories = [];
    const data = [];
    let startRow = -1;
    
    for (let i = 0; i < cashFlowData.length; i++) {
      if (cashFlowData[i][0] === 'Bridge Item' && cashFlowData[i][1] === 'Amount') {
        startRow = i + 1;
        break;
      }
    }
    
    if (startRow > 0) {
      for (let i = startRow; i < startRow + 7 && i < cashFlowData.length; i++) {
        if (cashFlowData[i][0]) {
          categories.push(cashFlowData[i][0]);
          data.push(parseFloat(cashFlowData[i][1]?.toString().replace(/,/g, '')) || 0);
        }
      }
    }
    
    return { categories, data };
  };

  const parseCashFlowData = () => {
    if (!cashFlowData || cashFlowData.length < 3) return { months: [], data: [] };
    
    let monthsRow = null;
    let closingCashRow = null;
    
    for (let i = 0; i < cashFlowData.length; i++) {
      const row = cashFlowData[i];
      if (row[1] && row[1].toString().includes('Apr')) {
        monthsRow = row;
      }
      if (row[0] === 'Closing Cash') {
        closingCashRow = row;
        break;
      }
    }
    
    if (!closingCashRow) return { months: [], data: [] };
    
    const months = monthsRow ? monthsRow.slice(1, 13) : 
                   ['Apr-25', 'May-25', 'Jun-25', 'Jul-25', 'Aug-25', 'Sep-25',
                    'Oct-25', 'Nov-25', 'Dec-25', 'Jan-26', 'Feb-26', 'Mar-26'];
    
    const data = [];
    for (let i = 0; i < 12; i++) {
      data.push(parseFloat(closingCashRow[i + 1]?.toString().replace(/,/g, '')) || 0);
    }
    
    return { months, data };
  };

  const getBarOption = (categories, data, title) => ({
    title: {
      text: title,
      left: 'center',
      top: 5,
      textStyle: {
        fontSize: 11,
        fontWeight: 'normal',
        color: '#64748b'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      textStyle: { fontSize: 10 }
    },
    grid: { 
      left: 35, 
      right: 15, 
      top: 30, 
      bottom: 45,
      containLabel: false
    },
    xAxis: { 
      type: 'category',
      data: categories,
      axisLabel: {
        rotate: 30,
        fontSize: 9,
        color: '#64748b'
      },
      axisLine: {
        lineStyle: { color: '#e2e8f0' }
      }
    },
    yAxis: { 
      type: 'value',
      axisLabel: {
        fontSize: 9,
        color: '#64748b',
        formatter: (value) => {
          if (Math.abs(value) >= 1000000) {
            return (value / 1000000).toFixed(1) + 'M';
          }
          if (Math.abs(value) >= 1000) {
            return (value / 1000).toFixed(0) + 'K';
          }
          return value;
        }
      },
      splitLine: {
        lineStyle: { color: '#f1f5f9' }
      }
    },
    series: [{
      type: 'bar',
      data: data,
      itemStyle: { color: '#2563eb' }
    }]
  });

  const getLineOption = (months, data, title) => ({
    title: {
      text: title,
      left: 'center',
      top: 5,
      textStyle: {
        fontSize: 11,
        fontWeight: 'normal',
        color: '#64748b'
      }
    },
    tooltip: {
      trigger: 'axis',
      textStyle: { fontSize: 10 }
    },
    grid: { 
      left: 35, 
      right: 15, 
      top: 30, 
      bottom: 35,
      containLabel: false
    },
    xAxis: { 
      type: 'category',
      data: months,
      boundaryGap: false,
      axisLabel: {
        rotate: 30,
        fontSize: 9,
        color: '#64748b'
      },
      axisLine: {
        lineStyle: { color: '#e2e8f0' }
      }
    },
    yAxis: { 
      type: 'value',
      axisLabel: {
        fontSize: 9,
        color: '#64748b',
        formatter: (value) => {
          if (Math.abs(value) >= 1000000) {
            return (value / 1000000).toFixed(1) + 'M';
          }
          if (Math.abs(value) >= 1000) {
            return (value / 1000).toFixed(0) + 'K';
          }
          return value;
        }
      },
      splitLine: {
        lineStyle: { color: '#f1f5f9' }
      }
    },
    series: [{
      type: 'line',
      data: data,
      smooth: true,
      lineStyle: { color: '#2563eb', width: 2 },
      itemStyle: { color: '#2563eb' },
      areaStyle: { color: 'rgba(37, 99, 235, 0.1)' }
    }]
  });

  const productData = parseProductData();
  const bridgeData = parseBridgeData();
  const cashData = parseCashFlowData();

  return (
    <div className="w-[30vw] bg-white border-l border-slate-200 flex flex-col">
      {/* Fixed Header */}
      <div className="bg-white p-4 pb-3 border-b border-slate-200 sticky top-0 z-10">
        <h3 className="text-sm font-semibold text-slate-700">Quick View</h3>
      </div>
      
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
        {productData.data.length > 0 ? (
          <ReactECharts 
            option={getBarOption(productData.categories, productData.data, 'Revenue by Product')} 
            style={{ height: '180px' }} 
            opts={{ renderer: 'svg' }} 
          />
        ) : (
          <div className="flex items-center justify-center h-40 text-slate-300 text-xs">No data</div>
        )}
      </div>

      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
        {bridgeData.data.length > 0 ? (
          <ReactECharts 
            option={getBarOption(bridgeData.categories, bridgeData.data, 'EBITDA Bridge')} 
            style={{ height: '180px' }} 
            opts={{ renderer: 'svg' }} 
          />
        ) : (
          <div className="flex items-center justify-center h-40 text-slate-300 text-xs">No data</div>
        )}
      </div>

      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
        {cashData.data.length > 0 ? (
          <ReactECharts 
            option={getLineOption(cashData.months, cashData.data, 'Monthly Closing Cash')} 
            style={{ height: '180px' }} 
            opts={{ renderer: 'svg' }} 
          />
        ) : (
          <div className="flex items-center justify-center h-40 text-slate-300 text-xs">No data</div>
        )}
      </div>

      <div className="text-xs text-slate-400 text-center pt-2 pb-2">
        View full dashboard for details
      </div>
    </div>
    </div>
  );
};

export default QuickGraphs;